import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { AlertType } from '@app/components/Alert';
import { H2, H3 } from '@app/components/typography';
import useGTMTracking from '@app/hooks/useGTMTracking';

import { useOrderHistoryQuery } from './__graphql__/OrderHistory';
import type {
  OrderHistoryOrdersFragment,
  OrderHistoryQuery,
} from './__graphql__/OrderHistory';
import type { ReturnRequestAlertType } from './ReturnRequest/components/ReturnRequestAlerts';
import { ReturnRequestAlerts } from './ReturnRequest/components/ReturnRequestAlerts';
import { useReturnRequestModal } from './ReturnRequest/hooks/useReturnRequestModal';
import messages from './OrderHistory.messages';
import { LoadMoreButton } from './OrderHistory.styles';
import OrderHistoryItems from './OrderHistoryItems';

const readOrders = (
  data?: OrderHistoryQuery,
): OrderHistoryOrdersFragment | null =>
  data?.magento2?.customer?.globalOrders ?? null;

const OrderHistory = () => {
  const [returnAlert, setReturnAlert] = useState<ReturnRequestAlertType>(null);
  const { data, fetchMore, loading, refetch } = useOrderHistoryQuery({
    ssr: false,
    variables: {
      currentPage: 1,
      orderStatuses: ACTIVE_ORDER_STATUSES,
    },
  });
  const { openReturnModal } = useReturnRequestModal();

  const orders = readOrders(data);
  const orderItems = readOrders(data)?.items;
  const hasNextPage = orderItems?.length !== orders?.totalCount;
  const loadMore = () =>
    fetchMore({
      variables: {
        currentPage: (readOrders(data)?.pageInfo?.currentPage ?? 1) + 1,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (
          hasNextPage &&
          fetchMoreResult?.magento2?.customer?.globalOrders?.items
        ) {
          const paginatedOrderItems = [
            ...(readOrders(previousResult)?.items ?? [null]),
            ...(readOrders(fetchMoreResult)?.items ?? [null]),
          ];

          fetchMoreResult.magento2.customer.globalOrders.items =
            paginatedOrderItems;
          return fetchMoreResult;
        }

        return previousResult;
      },
    });

  useGTMTracking({});

  function handleReturnRequestSuccess() {
    setReturnAlert(AlertType.Success);
    refetch();
  }

  function handleReturnRequestError() {
    setReturnAlert(AlertType.Error);
  }

  const triggerReturnModal = (orderId: string) => {
    const order = orderItems?.find((item) => item?.id === orderId);
    if (!order?.items || !data?.magento2?.customer?.email) return;
    openReturnModal({
      orderDate: order.orderDate,
      orderUid: order.id,
      orderProducts: order.items,
      contactEmail: data?.magento2?.customer?.email,
      onSuccess: handleReturnRequestSuccess,
      onError: handleReturnRequestError,
    });
  };

  if (loading) return null;

  return (
    <div>
      <ReturnRequestAlerts alert={returnAlert} />
      <H2 as="h1">
        <FormattedMessage {...messages.title} />
      </H2>

      <H3>
        <FormattedMessage {...messages.subTitle} />
      </H3>

      {orderItems?.length ? (
        <>
          <OrderHistoryItems
            items={orderItems}
            onClickReturn={triggerReturnModal}
          />
          {hasNextPage && (
            <LoadMoreButton wide type="primary" onClick={loadMore}>
              <span data-testid="load-more">
                <FormattedMessage {...messages.loadMore} />
              </span>
            </LoadMoreButton>
          )}
        </>
      ) : (
        <p>
          <span data-testid="empty-message">
            <FormattedMessage {...messages.empty} />
          </span>
        </p>
      )}
    </div>
  );
};

const ACTIVE_ORDER_STATUSES = [
  'closed',
  'complete',
  'error',
  'exported',
  'fraud',
  'holded',
  'holded_order_cancelled',
  'payment_review',
  'paypal_canceled_reversal',
  'paypal_reversed',
  'pending',
  'pending_payment',
  'pending_paypal',
  'processing',
  'received',
  'rejected',
];

export default OrderHistory;
